"use client";

import { useAccount, useBalance, useDisconnect } from "wagmi";
import { CloseIcon } from "../../../icons";
import { MetaMaskIcon } from "../../../icons";
import { WalletAvatarIcon } from "../../../icons";
import { CopyIcon, PowerIcon } from "lucide-react";
import { formatEther } from "viem";
import { formatBalanceWithOptionalDecimals } from "@/utils/string.utils";
import { StatusToast } from "@/components/toasts";
import { useToast } from "@/hooks";

interface WalletInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function WalletInfoModal({
  isOpen,
  onClose,
}: WalletInfoModalProps) {
  const { address, connector } = useAccount();
  const { disconnect } = useDisconnect();
  const { data: balance } = useBalance({ address });
  const { toast, showSuccess, showError, hide } = useToast();

  if (!isOpen || !address) return null;

  const handleDisconnect = async () => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    disconnect();
    onClose();
  };

  const formatAddress = (addr: string) => {
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  const copyToClipboard = async () => {
    if (!address) return;

    try {
      await navigator.clipboard.writeText(address);
      showSuccess("Wallet address copied to clipboard", "Copied");
    } catch {
      showError("Failed to copy wallet address", "Copy Failed");
    }
  };

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-black/50 p-4">
      <div className="relative flex flex-col justify-center items-center p-4 gap-3.5 w-[290px] bg-[#030213] border border-white/20 rounded-2xl">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-4 h-4 opacity-70 hover:opacity-100 transition-opacity z-20 cursor-pointer"
        >
          <CloseIcon />
        </button>

        {/* Wallet Icon with Badge */}
        <div className="relative w-12 h-[50px]">
          {/* Background Circle */}
          <div className="absolute w-12 h-12 bg-[rgba(14,150,111,0.24)] shadow-[inset_0px_0px_12px_rgba(255,255,255,0.25)] rounded-xl" />

          {/* Wallet Icon */}
          <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-6 h-6">
            <WalletAvatarIcon />
          </div>

          {/* MetaMask Badge (if MetaMask) */}
          {connector?.name === "MetaMask" && (
            <div className="absolute right-0 bottom-0 w-4 h-4 rounded-full flex items-center justify-center">
              <MetaMaskIcon />
            </div>
          )}
        </div>

        {/* Address with Copy Icon */}
        <div className="flex flex-row items-center gap-2.5">
          <span className="font-grotesk font-medium text-lg leading-5 tracking-[-0.15px] text-white">
            {formatAddress(address)}
          </span>
          <button
            onClick={copyToClipboard}
            className="w-4 h-4 opacity-60 hover:opacity-100 transition-opacity"
          >
            <CopyIcon size={16} color="#FFFFFF" />
          </button>
        </div>

        {/* Balance */}
        <span className="font-grotesk font-medium text-2xl leading-10 tracking-[-0.15px] text-white">
          {balance
            ? `${formatBalanceWithOptionalDecimals(formatEther(balance.value))} KROWN`
            : "0 KROWN"}
        </span>

        {/* Disconnect Button */}
        <button
          onClick={handleDisconnect}
          className="flex flex-row justify-center items-center px-4 py-2 gap-2 w-full max-w-[257px] h-[41px] bg-[rgba(251,44,54,0.2)] border border-[rgba(251,44,54,0.5)] rounded-lg hover:bg-[rgba(251,44,54,0.3)] transition-colors cursor-pointer"
        >
          <PowerIcon size={16} color="#FE5058" />
          <span className="font-vietnam font-medium text-sm leading-5 tracking-[-0.15px] text-[#FE5058]">
            Disconnect
          </span>
        </button>
      </div>
      <StatusToast
        isVisible={toast.isVisible}
        onClose={hide}
        isSuccess={toast.isSuccess}
        isLoading={toast.isLoading}
        title={toast.title}
        message={toast.message}
      />
    </div>
  );
}
